import React, { useMemo, useState, useRef, useEffect } from "react";
import { Fade, Typography, Box, Skeleton } from "@mui/material";
import { TableContainer as StyledTableContainer } from "./styles";
import { MaterialReactTable } from "material-react-table";
import ReactEcharts from "echarts-for-react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";

function TableView({
  dataLoading,
  summaryData,
  summaryColumns,
  currentChartOption,
  onChartReady,
  itemsData,
  selectedItems,
  displayDates,
}) {
  const [tableKey, setTableKey] = useState(0);
  const chartScrollRef = useRef(null);
  const tableContainerRef = useRef(null);
  const isSyncingRef = useRef(false);
  const groupNameDisplay = useMemo(() => {
    if (!selectedItems || selectedItems.length === 0) return "요약";
    return selectedItems.join(", ");
  }, [selectedItems]);
    const handlePanelResize = () => {
    setTableKey((prev) => prev + 1);
  };
  const chartWidth = useMemo(() => {
    return Math.max(displayDates.length * 80, 800);
  }, [displayDates]);
  useEffect(() => {
    const findTableContainer = () => {
      return tableContainerRef.current?.querySelector(".MuiTable-root")
        ?.parentElement;
    };
    const tableContainer = findTableContainer();
    const chartContainer = chartScrollRef.current;
    if (!tableContainer || !chartContainer) {
      const timer = setTimeout(() => {
        const retryTableContainer = findTableContainer();
        if (retryTableContainer && chartContainer) {
          setupScrollSync(retryTableContainer, chartContainer);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    setupScrollSync(tableContainer, chartContainer);
    function setupScrollSync(table, chart) {
      const handleTableScroll = () => {
        if (isSyncingRef.current) return;
        isSyncingRef.current = true;
        requestAnimationFrame(() => {
          if (chart.scrollLeft !== table.scrollLeft) {
            chart.scrollLeft = table.scrollLeft;
          }
          isSyncingRef.current = false;
        });
      };
      const handleChartScroll = () => {
        if (isSyncingRef.current) return;
        isSyncingRef.current = true;
        requestAnimationFrame(() => {
          if (table.scrollLeft !== chart.scrollLeft) {
            table.scrollLeft = chart.scrollLeft;
          }
          isSyncingRef.current = false;
        });
      };
      table.addEventListener("scroll", handleTableScroll);
      chart.addEventListener("scroll", handleChartScroll);
      return () => {
        table.removeEventListener("scroll", handleTableScroll);
        chart.removeEventListener("scroll", handleChartScroll);
      };
    }
  }, [tableKey, dataLoading]);
  return (
    <PanelGroup direction="vertical" style={{ height: "100%", width: "100%" }}>
      <Panel defaultSize={50} minSize={20} onResize={handlePanelResize}>
        <Fade in={!dataLoading} timeout={500}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <StyledTableContainer sx={{ flex: "0 0 auto" }}>
              <Typography variant="h6" gutterBottom sx={{ px: 2, pt: 2 }}>
                {groupNameDisplay}
              </Typography>
            </StyledTableContainer>
            {dataLoading ? (
              <Box sx={{ p: 2 }}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={200}
                  animation="wave"
                />
              </Box>
            ) : (
              <Box
                ref={tableContainerRef}
                sx={{ flex: "1 1 auto", overflow: "hidden" }}
              >
                <MaterialReactTable
                  key={tableKey}
                  columns={summaryColumns}
                  data={summaryData}
                  enableTopToolbar={false}
                  enableColumnActions={false}
                  enableColumnPinning
                  enableColumnFilters={false}
                  enablePagination={false}
                  enableSorting={false}
                  enableBottomToolbar={false}
                  enableRowSelection={false}
                  initialState={{ columnPinning: { left: ["type"] } }}
                  muiTableProps={{ sx: { tableLayout: "fixed" } }}
                  muiTableHeadCellProps={{
                    sx: { textAlign: "center", backgroundColor: "#f5f5f5" },
                  }}
                  muiTableHeadRowProps={{
                    sx: { "& > th": { borderRight: "1px solid #E0DEE3" } },
                  }}
                  muiTableBodyRowProps={{
                    sx: {
                      height: "30px !important",
                      maxHeight: "40px !important",
                      "& > td": {
                        height: "30px !important",
                        padding: "0px 16px",
                        borderRight: "1px solid #E0DEE3",
                      },
                      "&:hover": { backgroundColor: "#f5f5f5 !important" },
                    },
                  }}
                  muiTableBodyCellProps={{
                    sx: {
                      height: "40px !important",
                      padding: "0px 16px !important",
                      verticalAlign: "middle",
                      textAlign: "center",
                    },
                  }}
                  muiTableContainerProps={{
                    sx: {
                      height: "100%",
                      maxHeight: "100%",
                      overflow: "auto",
                      "&::-webkit-scrollbar": { width: "8px", height: "8px" },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(99, 96, 150, 0.7)",
                        borderRadius: "5px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent",
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Fade>
      </Panel>
      <PanelResizeHandle
        style={{
          position: "relative",
          height: 12,
          background: "transparent",
          cursor: "row-resize",
        }}
      >
        <Box
          sx={{
            mx: "auto",
            mt: "2px",
            width: 56,
            height: 6,
            borderRadius: 9999,
            bgcolor: "#E5E7EB",
            border: "1px solid #E5E7EB",
            transition: "all .15s ease",
            "&:hover": { bgcolor: "#D1D5DB", borderColor: "#D1D5DB" },
            "&:active": { bgcolor: "#9CA3AF", borderColor: "#9CA3AF" },
          }}
        />
      </PanelResizeHandle>
      <Panel defaultSize={50} minSize={20}>
        <Fade in={!dataLoading} timeout={700}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography variant="h6">예상재고 추이</Typography>
            </Box>
            {dataLoading ? (
              <Box
                sx={{
                  flex: "1 1 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 2,
                  pb: 2,
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  animation="wave"
                />
              </Box>
            ) : (
              <Box
                ref={chartScrollRef}
                sx={{
                  flex: "1 1 auto",
                  overflowX: "auto",
                  overflowY: "hidden",
                  height: "100%",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                <ReactEcharts
                  option={currentChartOption}
                  style={{
                    height: "100%",
                    width: `${chartWidth}px`,
                    minWidth: "100%",
                    marginLeft: "30px",
                  }}
                  opts={{ renderer: "canvas" }}
                  onChartReady={onChartReady}
                  notMerge
                  lazyUpdate={false}
                />
              </Box>
            )}
          </Box>
        </Fade>
      </Panel>
    </PanelGroup>
  );
}

export default TableView;
